import React, {useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native';

import CCText from '../../../Atoms/CCText';
import {CourseChapter} from '../../../Hooks/LmsCourseApi/types';
import {CoachTurn, useAskCoach} from '../../../Hooks/LmsCourseApi/useAskCoach';

import {CoachContext} from '../coachContext';
import {CoachStep} from '../coachNarration';

import {F} from '../courseTheme';
import CoachTurnEntry from './CoachTurnEntry';
import CourseButton from './CourseButton';
import CourseMicButton from './CourseMicButton';
import CourseSpeakingLine from './CourseSpeakingLine';

type Props = {
  bookUuid: string;
  chapters: CourseChapter[];
  /** Jump to a chapter by its position in `chapters`. */
  onGoToChapter: (index: number) => void;
  /**
   * Whether the panel is showing. Controlled by the screen, because the button
   * that opens it lives in the chapter header — at the bottom of the page it
   * fell below the fold on any chapter with a board on it.
   */
  open: boolean;
  /** The conversation so far, oldest first. Owned by the screen. */
  turns: CoachTurn[];
  onTurns: (next: CoachTurn[]) => void;
  /** Docked beside the lesson, or across the bottom on a narrow window. */
  mode?: 'rail' | 'sheet';
  /** How many chapters this coach can see, for the subtitle. */
  chapterCount?: number;
  /** The chapter on screen, so "why doesn't this work?" needs no preamble. */
  currentChapter?: number | null;
  onClose?: () => void;
  /** Play a narrated demonstration on the board. Owned by the screen, which
   *  is the thing that holds the board. */
  onPlayScript?: (steps: CoachStep[], turnIndex: number) => void;
  onStopScript?: () => void;
  /** Index of the answer currently being demonstrated, if any. */
  playingTurn?: number | null;
  /** Turn a spoken question into text. Absent when voice is unavailable. */
  onTranscribe?: (audio: Blob) => Promise<string | null>;
  /** The sentence being spoken right now, if a demonstration is playing. */
  speakingLine?: string | null;
  /**
   * What is on the student's screen at the moment they ask: the board and
   * its moves, or the second of the video. A getter, read on submit, so the
   * panel does not re-render on every video tick.
   */
  getContext?: () => CoachContext | null;
};

const SUGGESTIONS = [
  'What does this course cover?',
  'What should I review?',
  "Which puzzles haven't I solved?",
];

/**
 * Openers for when the coach already knows which chapter is on screen. They
 * say "this" on purpose — the point is that the student never has to explain
 * which chapter they mean.
 */
const SUGGESTIONS_IN_CONTEXT = [
  'What should I be looking for here?',
  'Why am I stuck on this one?',
  'What should I review before this?',
];

/**
 * Ask the coach about the course, rather than about one position.
 *
 * It is a conversation, not a series of unrelated questions: every turn is
 * kept and sent back with the next one, so "why that one?" resolves against
 * what was just said. Nothing is stored — not on the server, not in this
 * browser — so the thread lasts exactly as long as the page does, which the
 * panel says out loud rather than letting a student discover it.
 *
 * Chapter references become buttons. Every number was checked server-side
 * against the chapters this course actually has, and is resolved here by
 * matching `chapter_number` rather than by assuming chapter N is the Nth
 * entry — a course with a gap in its numbering would otherwise send the
 * student one chapter off.
 */
export default function CourseCoachPanel({
  bookUuid,
  chapters,
  onGoToChapter,
  open,
  turns,
  onTurns,
  mode = 'rail',
  chapterCount,
  currentChapter,
  onClose,
  onPlayScript,
  onStopScript,
  playingTurn = null,
  onTranscribe,
  speakingLine = null,
  getContext,
}: Props) {
  const [question, setQuestion] = useState('');
  const [recording, setRecording] = useState(false);
  const ask = useAskCoach();

  // Take the student to whatever just arrived. Unconditionally, not only when
  // they are already near the bottom: they pressed a button asking for help,
  // so they get shown the help. Without this the panel silently does nothing
  // visible on the second hint of a long conversation — the entry is appended
  // below the fold of a transcript nobody has scrolled.
  const transcriptRef = useRef<ScrollView>(null);
  useEffect(() => {
    if (!turns.length) return;
    const id = setTimeout(
      () => transcriptRef.current?.scrollToEnd({animated: true}),
      60,
    );
    return () => clearTimeout(id);
  }, [turns.length]);

  const failed = !!ask.error;

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || ask.isLoading) return;
    setQuestion('');
    ask.mutate(
      {
        bookUuid,
        question: trimmed,
        history: turns,
        currentChapter,
        context: getContext?.() ?? null,
      },
      {
        onSuccess: answer =>
          onTurns([
            ...turns,
            {
              question: trimmed,
              answer: answer.answer,
              chapters: answer.chapters ?? [],
              steps: (answer.steps ?? []) as CoachStep[],
            },
          ]),
        // A failed turn is deliberately not appended: sending "you did not
        // answer" back as context on the next question teaches the model that
        // refusing is the shape of a reply here.
      },
    );
  };

  const jumpsFor = (numbers: number[]) =>
    (numbers ?? [])
      .map(number => ({
        number,
        index: chapters.findIndex(c => c.chapter_number === number),
      }))
      // A number the course does not have is dropped rather than shown as a
      // button that goes nowhere. The server checks this too; this is the
      // second gate, because the cost of being wrong lands on the student.
      .filter(j => j.index >= 0);

  if (!open) return null;

  return (
    // `minHeight: 0` is load-bearing. Without it a flex child is floored at
    // its content height, so the transcript below grows the panel instead of
    // scrolling inside it — the conversation just ran off the bottom.
    <View style={{flex: 1, minHeight: 0}}>
      {/* Header strip. The subtitle is what tells a student this is not the
          same thing as "Ask about this position" inside the puzzle card. */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          backgroundColor: F.badgeBg,
          borderTopLeftRadius: F.radius.card,
          borderTopRightRadius: mode === 'sheet' ? F.radius.card : 0,
          paddingHorizontal: 14,
          paddingVertical: 10,
        }}>
        <View style={{flex: 1}}>
          <CCText style={{fontSize: 15, fontWeight: '600', color: F.brownDark}}>
            Your course coach
          </CCText>
          <CCText style={{fontSize: 12, fontWeight: '500', color: F.mutedText}}>
            {chapterCount
              ? `Knows all ${chapterCount} chapters and your progress`
              : 'Knows the whole course and your progress'}
          </CCText>
        </View>
        {onClose ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close the coach"
            onPress={onClose}
            style={{padding: 6}}>
            <CCText
              style={{fontSize: 16, fontWeight: '600', color: F.mutedText}}>
              ✕
            </CCText>
          </Pressable>
        ) : null}
      </View>

      <ScrollView
        ref={transcriptRef}
        style={{flex: 1, minHeight: 0}}
        contentContainerStyle={{padding: 14, gap: 10}}>
      {turns.length ? (
        <View style={{gap: 10}}>
          {turns.map((turn, i) => {
            const jumps = jumpsFor(turn.chapters);
            const isHint = turn.kind === 'hint';
            return (
              <CoachTurnEntry key={`${i}-${turn.question}`} isNewest={i === turns.length - 1}>
                {/* A hint was asked for by pressing a button on the board, so
                    it is labelled as what it is rather than quoted back as a
                    question the student typed. */}
                <CCText
                  style={{
                    fontSize: 13,
                    fontWeight: '600',
                    color: isHint ? F.hintFg : F.mutedText,
                  }}>
                  {turn.question}
                </CCText>
                <View
                  style={{
                    backgroundColor: isHint ? F.hintBg : F.badgeBg,
                    borderLeftWidth: isHint ? 3 : 0,
                    borderLeftColor: F.hintFg,
                    borderRadius: 8,
                    padding: 14,
                    gap: 10,
                  }}>
                  <CCText
                    style={{
                      fontSize: 14,
                      fontWeight: '500',
                      color: F.brown,
                      lineHeight: 22,
                    }}>
                    {turn.answer}
                  </CCText>

                  {/* A demonstration the student can watch and hear. The
                      moves were replayed on a real board server-side, so
                      what plays here cannot be a line that does not exist. */}
                  {turn.steps?.length && onPlayScript ? (
                    <View style={{gap: 6}}>
                      <CourseButton
                        label={
                          playingTurn === i
                            ? 'Stop'
                            : `Show me on the board (${turn.steps.length})`
                        }
                        variant="review"
                        onPress={() =>
                          playingTurn === i
                            ? onStopScript?.()
                            : onPlayScript(turn.steps!, i)
                        }
                      />
                      {/* The moves in notation, from the server's own replay.
                          The spoken sentence can overclaim — the model writes
                          "Qd8+" where the board says "Qd8" — so this is the
                          version that is true of the position. */}
                      <CCText
                        style={{fontSize: 12, fontWeight: '600', color: F.mutedText}}>
                        {turn.steps
                          .map(s => s.san)
                          .filter(Boolean)
                          .join('  ')}
                      </CCText>
                      {playingTurn === i && speakingLine ? (
                        <CourseSpeakingLine text={speakingLine} />
                      ) : null}
                    </View>
                  ) : null}

                  {jumps.length ? (
                    <View style={{gap: 6}}>
                      <CCText
                        style={{
                          fontSize: 12,
                          fontWeight: '600',
                          color: F.mutedText,
                        }}>
                        GO TO
                      </CCText>
                      {/* Stacked, not wrapped: the rail is ~320px and a
                          chapter title is a sentence, so a row of these
                          overflowed its right edge. */}
                      <View style={{gap: 8}}>
                        {jumps.map(j => (
                          <CourseButton
                            key={j.number}
                            label={`${j.number}. ${chapters[j.index].title}`}
                            variant="review"
                            onPress={() => onGoToChapter(j.index)}
                          />
                        ))}
                      </View>
                    </View>
                  ) : null}
                </View>
              </CoachTurnEntry>
            );
          })}
        </View>
      ) : null}

      {!turns.length && !ask.isLoading ? (
        <View style={{flexDirection: 'row', gap: 8, flexWrap: 'wrap'}}>
          {(currentChapter ? SUGGESTIONS_IN_CONTEXT : SUGGESTIONS).map(s => (
            <Pressable
              accessibilityRole="button"
              key={s}
              onPress={() => submit(s)}
              style={{
                backgroundColor: F.badgeBg,
                borderRadius: 999,
                paddingHorizontal: 12,
                paddingVertical: 6,
              }}>
              <CCText style={{fontSize: 12, fontWeight: '500', color: F.brown}}>
                {s}
              </CCText>
            </Pressable>
          ))}
        </View>
      ) : null}

      {ask.isLoading ? <ActivityIndicator color={F.teal} /> : null}

      {failed ? (
        <View
          style={{
            backgroundColor: F.wrongBg,
            borderWidth: 1,
            borderColor: F.wrongFg,
            borderRadius: 6,
            padding: 12,
          }}>
          <CCText style={{fontSize: 13, fontWeight: '500', color: F.wrongFg}}>
            {/* The server answers with nothing rather than guessing about a
                course it cannot see, so this is an honest failure. */}
            {(ask.error as any)?.response?.data?.error ??
              'Could not answer that one. Try rephrasing it.'}
          </CCText>
        </View>
      ) : null}

      </ScrollView>

      {/* The composer is pinned below the transcript rather than scrolling with
          it: it is where the mic will live, and a mic that drifts further down
          the page with every answer is a mic nobody finds twice. */}
      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: F.border10,
          padding: 14,
          gap: 8,
        }}>
        <View style={{flexDirection: 'row', gap: 8, alignItems: 'center'}}>
          {/* While recording, the bar takes the whole row: a text box nobody
              can type in next to a live waveform is just clutter. */}
          {recording ? null : (
            <TextInput
              value={question}
              onChangeText={setQuestion}
              onSubmitEditing={() => submit(question)}
              placeholder={
                turns.length ? 'Ask a follow-up…' : 'What should I go back to?'
              }
              accessibilityLabel="Your question for the coach"
              returnKeyType="send"
              style={{
                flex: 1,
                borderWidth: 1,
                borderColor: 'rgba(38,35,34,0.2)',
                borderRadius: 8,
                paddingHorizontal: 12,
                paddingVertical: 10,
                fontSize: 14,
              }}
            />
          )}
          {/* Shown only once there is something to send: a permanently
              disabled button is furniture, not an affordance. The mic takes
              its place while the box is empty, which is also when someone is
              most likely to want to just say it. */}
          {question.trim() && !recording ? (
            <CourseButton
              label={ask.isLoading ? 'Asking…' : 'Ask'}
              disabled={!question.trim() || ask.isLoading}
              onPress={() => submit(question)}
            />
          ) : null}
          {onTranscribe && !question.trim() ? (
            <CourseMicButton
              disabled={ask.isLoading}
              onTranscribe={onTranscribe}
              onRecordingChange={setRecording}
              // Spoken questions are asked straight away. Dropping the words
              // into the box and waiting for a second press makes talking
              // slower than typing, which defeats the point.
              onText={text => submit(text)}
            />
          ) : null}
        </View>

        {/* Stated once, up front — not sprung on a student on the way out,
            by which time the conversation is already gone. */}
        <CCText style={{fontSize: 11, fontWeight: '500', color: F.mutedText}}>
          This chat isn't saved — it clears if you leave the course or refresh.
        </CCText>
      </View>
    </View>
  );
}
